/**
 * Tag — Henaket tag/chip component.
 *
 * 0.36.265 — Implementation of the Henaket tag spec.
 *
 * Two visual variants:
 *   - `variant="filled"` (default): "Tags Standard" / "Tags + Icon"
 *     — filled blue-grey background, no border. The default chip
 *     look for selected items in multi-select dropdowns and for
 *     status badges.
 *   - `variant="outlined"`: "More Tags" — white background, 1 px
 *     grey-blue border. Used for overflow indicators ("+3 more")
 *     and any tag that should read as a placeholder rather than
 *     a real selection.
 *
 * Sizing (both variants):
 *   - Height 32 px, border-radius 4 px.
 *   - Horizontal padding 8 px each side, vertical 4 px inside
 *     the content row.
 *   - Title text: 16 / 600 / 24 / #1A1A1A (Semibold Body Text).
 *   - Optional close button: 16 × 16 px with an × in #1A1A1A.
 *   - Gap between title and close: 8 px.
 *
 * Filled-variant state colours:
 *   - Default:  #CBD6E3 (light blue-grey)
 *   - Hover:    #A5B7CD (medium blue-grey)
 *   - Active:   #8099B8 (dark blue-grey)
 *   - Disabled: opacity 0.5
 *
 * Focus ring (filled only, when interactive):
 *   - 4 px solid #BD13B8 (magenta) sitting 5 px above/below and
 *     13 px to either side of the tag, border-radius 8 px.
 *   - Visible only on keyboard focus (:focus-visible).
 *
 * The tag itself is `display: inline-flex` and renders as a
 * `<span>` (or `<button>` when interactive — handled by callers
 * passing `onClick`). The close button is a real `<button>` so
 * the × is keyboard-reachable independently of the tag.
 */
import styles from './Tag.module.css';

export default function Tag({
  children,
  variant = 'filled',  // 'filled' | 'outlined'
  closable = false,
  onClose,             // (e) => void  — called when × is clicked
  closeAriaLabel,      // accessibility label for the × button
  disabled = false,
  className = '',
  ...rest
}) {
  const cls = [
    styles.tag,
    styles[variant],
    disabled ? styles.disabled : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <span className={cls} {...rest}>
      <span className={styles.content}>
        <span className={styles.title}>{children}</span>
        {closable && (
          <button
            type="button"
            className={styles.closeBtn}
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled && onClose) onClose(e);
            }}
            disabled={disabled}
            aria-label={closeAriaLabel || 'Remove tag'}
            tabIndex={disabled ? -1 : 0}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              {/* × icon — two crossed lines centred in the 16×16 box. */}
              <path
                d="M4 4 L 12 12 M 12 4 L 4 12"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </span>
    </span>
  );
}
